import { Injectable, isDevMode } from '@angular/core';
import {
  CurrentUserQuery,
  Membership,
  StoreItemDetailFragment,
} from '../graphql/generated';
import { resolveStoreItemDiscount } from '../utils/store-item-pricing';

type UserPayload = {
  user_id?: string;
  user_role?: string;
};

// Set only on events emitted from the anonymous /public surface. Its absence
// means "not the public surface" — which also covers an authenticated visitor
// whose `me` has not resolved yet.
type SurfacePayload = {
  webinar_surface?: 'public';
};

const PUBLIC_SURFACE = {
  webinar_surface: 'public',
} as const satisfies SurfacePayload;

type EcommerceItems = [
  { item_id: string; item_name: string; price: number; quantity: 1 },
];

type EcommercePayload = {
  ecommerce: {
    currency: 'CZK';
    value: number;
    original_value: number;
    items: EcommerceItems;
  };
};

type WebinarJoinEvent = UserPayload &
  SurfacePayload & {
    event: 'webinar_join';
    webinar_id: string;
    webinar_title: string;
  };

type WebinarRegisterEvent = UserPayload &
  SurfacePayload & {
    event: 'webinar_register';
    webinar_id: string;
    webinar_title: string;
  };

type WebinarWatchMilestoneEvent = UserPayload & {
  event: 'webinar_watch_milestone';
  webinar_id: string;
  webinar_title: string;
  minutes_watched: number;
};

type ItemViewEvent = UserPayload & EcommercePayload & { event: 'view_item' };
type ItemBuyClickEvent = UserPayload &
  EcommercePayload & { event: 'add_to_cart' };
type CheckoutStartEvent = UserPayload &
  EcommercePayload & { event: 'begin_checkout' };
type CheckoutProceedEvent = UserPayload &
  EcommercePayload & { event: 'add_payment_info' };
type PurchaseEvent = UserPayload &
  EcommercePayload & { event: 'purchase'; transaction_id: string };

// The konfigurátor funnel (#879). The surface marker is reused so the public and
// authenticated funnels stay separable in GA4, exactly as the webinar events do.
type ConfiguratorOpenEvent = UserPayload &
  SurfacePayload & { event: 'configurator_open'; webinar_id: string };

type ConfiguratorStepEvent = UserPayload &
  SurfacePayload & {
    event: 'configurator_step';
    webinar_id: string;
    step: number;
  };

type ConfiguratorSubmitEvent = UserPayload &
  SurfacePayload & { event: 'configurator_submit'; webinar_id: string };

type AnalyticsEvent =
  | WebinarJoinEvent
  | WebinarRegisterEvent
  | WebinarWatchMilestoneEvent
  | ItemViewEvent
  | ItemBuyClickEvent
  | CheckoutStartEvent
  | CheckoutProceedEvent
  | PurchaseEvent
  | ConfiguratorOpenEvent
  | ConfiguratorStepEvent
  | ConfiguratorSubmitEvent;

declare global {
  // Declared identically in typings.d.ts, but the Storybook tsconfig does not
  // include that file, so it is redeclared here to keep both builds typed.
  // Global augmentation of Window must use an interface.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

type PricedItem = Pick<
  StoreItemDetailFragment,
  | 'id'
  | 'title'
  | 'priceWithVAT'
  | 'discountPriceWithVAT'
  | 'firstPriceWithVAT'
  | 'priceForMembersWithVAT'
>;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly environment = isDevMode() ? 'development' : 'production';

  private push(data: AnalyticsEvent): void {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ ...data, environment: this.environment });
  }

  private pushEcommerce(data: AnalyticsEvent): void {
    window.dataLayer = window.dataLayer ?? [];
    // Clear previous ecommerce object per GA4 recommendation
    window.dataLayer.push(
      { ecommerce: null },
      { ...data, environment: this.environment },
    );
  }

  identifyUser(user: CurrentUserQuery['me'] | null | undefined): void {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ user_id: user?.id ?? undefined });
  }

  private userPayload(
    user: CurrentUserQuery['me'] | null | undefined,
  ): UserPayload {
    if (!user) {
      return {};
    }
    return {
      user_id: user.id,
      user_role: user.role ?? undefined,
    };
  }

  private determinePrice(
    item: PricedItem,
    user: CurrentUserQuery['me'] | null | undefined,
    priceOverride?: number | null,
  ): number {
    if (priceOverride != null) {
      return priceOverride;
    }
    const discount = resolveStoreItemDiscount(
      item,
      user != null && user.membership !== Membership.Free,
    );
    return discount?.finalPriceWithVAT ?? item.priceWithVAT;
  }

  // priceOverride is used to track price after a promo code, as this info can't be in StoreItem
  private ecommercePayload(
    item: PricedItem,
    user: CurrentUserQuery['me'] | null | undefined,
    priceOverride?: number | null,
  ): EcommercePayload {
    const price = this.determinePrice(item, user, priceOverride);
    return {
      ecommerce: {
        currency: 'CZK',
        value: price,
        original_value: item.priceWithVAT,
        items: [
          { item_id: item.id, item_name: item.title ?? '', price, quantity: 1 },
        ],
      },
    };
  }

  trackWebinarJoin(
    webinarId: string,
    webinarTitle: string,
    user: CurrentUserQuery['me'] | null | undefined,
  ): void {
    this.push({
      event: 'webinar_join',
      webinar_id: webinarId,
      webinar_title: webinarTitle,
      ...this.userPayload(user),
    });
  }

  trackWebinarRegister(
    webinarId: string,
    webinarTitle: string,
    user: CurrentUserQuery['me'] | null | undefined,
  ): void {
    this.push({
      event: 'webinar_register',
      webinar_id: webinarId,
      webinar_title: webinarTitle,
      ...this.userPayload(user),
    });
  }

  // The /public surface has no user by construction: authGuard bounces
  // authenticated visitors off /public/* and the public registration creates no
  // account. Hence no user parameter — the payload is deliberately what
  // userPayload(null) already produces, plus the surface flag.
  trackPublicWebinarJoin(webinarId: string, webinarTitle: string): void {
    this.push({
      event: 'webinar_join',
      webinar_id: webinarId,
      webinar_title: webinarTitle,
      ...PUBLIC_SURFACE,
    });
  }

  trackPublicWebinarRegister(webinarId: string, webinarTitle: string): void {
    this.push({
      event: 'webinar_register',
      webinar_id: webinarId,
      webinar_title: webinarTitle,
      ...PUBLIC_SURFACE,
    });
  }

  // The card renders on the authenticated stream page as well as on /public, so the user is
  // taken here like every other authenticated event: without user_role the configurator
  // funnel cannot be segmented the way the webinar funnel is. Null on the public surface,
  // where userPayload contributes nothing and the surface marker carries the distinction.
  trackConfiguratorOpen(
    webinarId: string,
    isPublic: boolean,
    user: CurrentUserQuery['me'] | null | undefined,
  ): void {
    this.push({
      event: 'configurator_open',
      webinar_id: webinarId,
      ...(isPublic ? PUBLIC_SURFACE : {}),
      ...this.userPayload(user),
    });
  }

  trackConfiguratorStep(
    webinarId: string,
    step: number,
    isPublic: boolean,
    user: CurrentUserQuery['me'] | null | undefined,
  ): void {
    this.push({
      event: 'configurator_step',
      webinar_id: webinarId,
      step,
      ...(isPublic ? PUBLIC_SURFACE : {}),
      ...this.userPayload(user),
    });
  }

  trackConfiguratorSubmit(
    webinarId: string,
    isPublic: boolean,
    user: CurrentUserQuery['me'] | null | undefined,
  ): void {
    this.push({
      event: 'configurator_submit',
      webinar_id: webinarId,
      ...(isPublic ? PUBLIC_SURFACE : {}),
      ...this.userPayload(user),
    });
  }

  trackWebinarWatchMilestone(
    webinarId: string,
    webinarTitle: string,
    minutesWatched: number,
    user: CurrentUserQuery['me'] | null | undefined,
  ): void {
    this.push({
      event: 'webinar_watch_milestone',
      webinar_id: webinarId,
      webinar_title: webinarTitle,
      minutes_watched: minutesWatched,
      ...this.userPayload(user),
    });
  }

  trackItemView(
    item: PricedItem,
    user: CurrentUserQuery['me'] | null | undefined,
  ): void {
    this.pushEcommerce({
      event: 'view_item',
      ...this.ecommercePayload(item, user),
      ...this.userPayload(user),
    });
  }

  trackItemBuyClick(
    item: PricedItem,
    user: CurrentUserQuery['me'] | null | undefined,
  ): void {
    this.pushEcommerce({
      event: 'add_to_cart',
      ...this.ecommercePayload(item, user),
      ...this.userPayload(user),
    });
  }

  trackCheckoutStart(
    item: PricedItem,
    user: CurrentUserQuery['me'] | null | undefined,
  ): void {
    this.pushEcommerce({
      event: 'begin_checkout',
      ...this.ecommercePayload(item, user),
      ...this.userPayload(user),
    });
  }

  trackCheckoutProceed(
    item: PricedItem,
    user: CurrentUserQuery['me'] | null | undefined,
    priceOverride?: number | null,
  ): void {
    this.pushEcommerce({
      event: 'add_payment_info',
      ...this.ecommercePayload(item, user, priceOverride),
      ...this.userPayload(user),
    });
  }

  trackPurchaseComplete(
    item: PricedItem,
    user: CurrentUserQuery['me'] | null | undefined,
    transactionId: string,
    priceOverride?: number | null,
  ): void {
    this.pushEcommerce({
      event: 'purchase',
      transaction_id: transactionId,
      ...this.ecommercePayload(item, user, priceOverride),
      ...this.userPayload(user),
    });
  }
}
